// ── LanguageContext.jsx ──────────────────────────────────────────────────────
// Provides language state (ar | en), persistence via localStorage,
// and a t() helper that resolves translation keys.

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import translations from "./translations";

// ── Constants ─────────────────────────────────────────────────────────────────
const LS_KEY = "app_language";          // localStorage key
const LS_CHOSEN_KEY = "app_lang_chosen"; // flag: user already chose a language

// ── Helper: apply lang attribute to <html> only (NO dir change) ──────────────
// تغيير اللغة يؤثر على النصوص فقط — اتجاه العناصر ثابت دائماً (RTL)
// هذا النهج المتبع في المواقع الاحترافية: العناصر لا تتأثر، النصوص فقط تتغير
function applyHtmlAttributes(language) {
  document.documentElement.setAttribute("lang", language);
  // dir ثابت على RTL دائماً — لا يتغير بتغيير اللغة
  document.documentElement.setAttribute("dir", "rtl");
}

// ── Context creation ──────────────────────────────────────────────────────────
export const LanguageContext = createContext({
  lang: "ar",
  setLang: () => {},
  t: () => "",
  isRTL: true,
  hasChosen: false,
  setHasChosen: () => {},
});

// ── Provider ──────────────────────────────────────────────────────────────────
export const LanguageProvider = ({ children }) => {
  // Read saved language from localStorage (default: "ar")
  const [lang, setLangState] = useState(() => {
    try {
      const saved = localStorage.getItem(LS_KEY);
      if (saved === "ar" || saved === "en") return saved;
    } catch {}
    return "ar";
  });

  // Whether user has already made a language choice (skip welcome screen)
  const [hasChosen, setHasChosenState] = useState(() => {
    try {
      return localStorage.getItem(LS_CHOSEN_KEY) === "true";
    } catch {}
    return false;
  });

  // Persist language to localStorage whenever it changes
  const setLang = useCallback((newLang) => {
    if (newLang !== "ar" && newLang !== "en") return;
    try {
      localStorage.setItem(LS_KEY, newLang);
    } catch {}
    setLangState(newLang);
  }, []);

  // Persist hasChosen flag
  const setHasChosen = useCallback((val) => {
    try {
      localStorage.setItem(LS_CHOSEN_KEY, String(val));
    } catch {}
    setHasChosenState(val);
  }, []);

  // Apply dir attribute to <html> element when language changes
  useEffect(() => {
    applyHtmlAttributes(lang);
  }, [lang]);

  // ── Fix: Re-sync language from localStorage when tab regains focus ──────────
  // This fixes the glitch where returning from a popunder ad (which may open
  // a new tab or trigger a page-show event) causes the html dir/lang to reset.
  useEffect(() => {
    const syncLangFromStorage = () => {
      try {
        const saved = localStorage.getItem(LS_KEY);
        const validLang = (saved === "ar" || saved === "en") ? saved : "ar";
        // Re-apply html attributes regardless (fixes any external reset)
        applyHtmlAttributes(validLang);
        // Update React state only if it actually changed
        setLangState((current) => {
          if (current !== validLang) return validLang;
          return current;
        });
      } catch {}
    };

    // Listen for visibility changes (tab becomes visible again after popunder)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncLangFromStorage();
      }
    };

    // Listen for window focus (browser window regains focus)
    const handleWindowFocus = () => {
      syncLangFromStorage();
    };

    // Listen for pageshow (back/forward navigation, including from popunder)
    const handlePageShow = (e) => {
      syncLangFromStorage();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleWindowFocus);
    window.addEventListener("pageshow", handlePageShow);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleWindowFocus);
      window.removeEventListener("pageshow", handlePageShow);
    };
  }, []);

  // ── Translation function ────────────────────────────────────────────────────
  // Usage: t("home.heroTitle1")  or  t("bypass.rewardDesc", { seconds: 5 })
  const t = useCallback(
    (key, params = {}) => {
      // Support dot-notation keys like "section.subKey"
      const parts = key.split(".");
      let node = translations;
      for (const part of parts) {
        if (node && typeof node === "object" && part in node) {
          node = node[part];
        } else {
          // Key not found — return the key itself as fallback
          return key;
        }
      }
      // node should now be { ar: "...", en: "..." }
      if (node && typeof node === "object" && (node.ar !== undefined || node.en !== undefined)) {
        let text = node[lang] ?? node["ar"] ?? key;
        // Simple interpolation: replace {param} placeholders
        Object.entries(params).forEach(([k, v]) => {
          text = text.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        });
        return text;
      }
      return key;
    },
    [lang]
  );

  const isRTL = lang === "ar";

  return (
    <LanguageContext.Provider
      value={{ lang, setLang, t, isRTL, hasChosen, setHasChosen }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

// ── Convenience hook ──────────────────────────────────────────────────────────
export const useLanguage = () => useContext(LanguageContext);
